"use client";

/* eslint-disable react-hooks/set-state-in-effect -- localStorage restores the last route after hydration */

import type { KeyboardEvent as ReactKeyboardEvent, ReactNode } from "react";
import Image from "next/image";
import Link from "next/link";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  CalendarDays,
  FolderOpen,
  House,
  MessageSquare,
  UserRound,
} from "lucide-react";

import { AiFloatingAssistant } from "@/components/ai/AiFloatingAssistant";
import { NotificationPermissionPrompt } from "@/components/NotificationPermissionPrompt";
import { useGlobalPreferences } from "@/components/useGlobalPreferences";
import { useI18n } from "@/components/useI18n";
import {
  AiSearchIcon,
  ChevronDownIcon,
  NotificationIcon,
  SearchIcon,
} from "@/components/icons/TairohIcons";
import { fetchJson } from "@/lib/api";
import { playNotificationChime, primeNotificationSound, showSystemNotification } from "@/lib/notification-sound";
import { SYSTEM_MESSAGE_EVENT, clearSystemMessages, countUnseenSystemMessages, listSystemMessages, markSystemMessagesSeen, type SystemMessage } from "@/lib/system-messages";
import { showNotificationToast } from "@/lib/toast";
import { currentPushSubscription } from "@/lib/push/client";
import { translateEventType } from "@/lib/i18n";
import clsx from "clsx";

const sections = [
  { key: "home", label: "home", Icon: House },
  { key: "chat", label: "chat", Icon: MessageSquare },
  { key: "calendar", label: "calendar", Icon: CalendarDays },
  { key: "files", label: "files", Icon: FolderOpen },
  { key: "me", label: "my page", Icon: UserRound },
] as const;

const mobileSections = ["chat", "calendar", "home", "files", "me"] as const;
const adminMobileSections = ["admin"] as const;

const searchCategories = [
  { key: "all", label: "すべて", types: [] },
  { key: "chat", label: "チャット", types: ["channel", "dm"] },
  { key: "calendar", label: "予定・記録", types: ["calendar", "task"] },
  {
    key: "files",
    label: "ファイル",
    types: ["html_document", "file", "folder", "microsoft365_file"],
  },
  { key: "member", label: "メンバー", types: ["member", "workspace"] },
  { key: "notification", label: "通知", types: ["notification"] },
  { key: "settings", label: "設定", types: ["setting"] },
  { key: "ai", label: "AI履歴", types: ["ai"] },
] as const;

type SectionKey = (typeof sections)[number]["key"];
type SearchCategory = (typeof searchCategories)[number]["key"];

type Workspace = {
  slug: string;
  name: string;
  icon_url?: string | null;
  accent_color?: string | null;
};

type SearchResult = {
  id: string;
  type: string;
  label: string;
  detail: string;
  href: string;
  workspace_slug?: string;
};

type NotificationEvent = {
  id: number | string;
  event_type?: string;
  title?: string;
  body?: string;
  url?: string;
  workspace_name?: string;
  read_at?: number | null;
  created_at?: number | string;
};

/** HH:MM for the notification tray. */
function formatNotificationTime(value: number | string) {
  const timestamp = Number(value);
  if (!Number.isFinite(timestamp) || timestamp <= 0) return "";
  return new Date(timestamp).toLocaleTimeString("ja-JP", { hour: "2-digit", minute: "2-digit" });
}

type ShellUser = {
  id: string;
  name: string;
  role?: string;
  avatarUrl?: string | null;
};

export function AppUnifiedShell({
  children,
  scope,
  workspaces,
  user,
}: {
  children: ReactNode;
  scope: string;
  workspaces: Workspace[];
  user: ShellUser;
}) {
  const pathname = usePathname();
  const router = useRouter();
  const searchParams = useSearchParams();
  const prefs = useGlobalPreferences();
  const { t } = useI18n();
  const en = prefs.locale === "en";
  const activeSection = getActiveSection(pathname, searchParams);
  const currentWorkspace = workspaces.find(
    (workspace) => workspace.slug === scope,
  );
  const selectedWorkspace =
    scope === "all"
      ? undefined
      : currentWorkspace
        ? { slug: currentWorkspace.slug, name: currentWorkspace.name }
        : undefined;
  const workspaceLabel =
    scope === "all" ? t("workspaceWide") : (currentWorkspace?.name ?? scope);
  const isGlobalScope = scope === "all";
  const workspaceMenuRef = useRef<HTMLDetailsElement>(null);
  const notificationRef = useRef<HTMLDivElement>(null);
  const lastNotificationId = useRef<number | null>(null);
  // Push購読の有無。購読済みならOS通知は Service Worker が担当する。
  const pushActiveRef = useRef(false);
  const [notificationTab, setNotificationTab] = useState<"events" | "system">("events");
  const [systemMessages, setSystemMessages] = useState<SystemMessage[]>([]);
  const [unseenSystemMessages, setUnseenSystemMessages] = useState(0);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [workspaceMenuOpen, setWorkspaceMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchCategory, setSearchCategory] = useState<SearchCategory>("all");
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState(false);
  const [notifications, setNotifications] = useState<{
    unread: number;
    events: NotificationEvent[];
  }>({ unread: 0, events: [] });
  const [showSearch, setShowSearch] = useState(false);
  const [searchMode, setSearchMode] = useState<"search" | "ai">("search");
  const [showNotifications, setShowNotifications] = useState(false);
  const [lastRoutes, setLastRoutes] = useState<Record<string, string>>({});
  const [surfaceNavigationOrder, setSurfaceNavigationOrder] = useState<"destination_then_previous" | "previous_then_destination">("destination_then_previous");
  const [previousSurfaceRoute, setPreviousSurfaceRoute] = useState<{ section: SectionKey; route: string } | null>(null);
  const [selectedCalendarEntity, setSelectedCalendarEntity] = useState<string | null>(null);
  const previousSurfaceRef = useRef<{ section: SectionKey; route: string } | null>(null);
  const routeKey = `${scope}:${activeSection}`;
  const currentRoute = `${pathname}${searchParams.toString() ? `?${searchParams.toString()}` : ""}`;

  useEffect(() => {
    try {
      setLastRoutes(
        JSON.parse(
          window.localStorage.getItem("hub:last-routes") ?? "{}",
        ) as Record<string, string>,
      );
    } catch {
      // Local route history is optional.
    }
  }, [scope]);

  useEffect(() => {
    try {
      const saved = JSON.parse(window.localStorage.getItem(`hub:calendar-selection:${scope}`) ?? "[]");
      setSelectedCalendarEntity(Array.isArray(saved) && saved.length ? String(saved[0]) : null);
    } catch {
      setSelectedCalendarEntity(null);
    }
  }, [scope]);

  useEffect(() => {
    void fetchJson<{ settings?: Record<string, unknown> }>(`/api/workspaces/${encodeURIComponent(scope)}/me/home-preferences`)
      .then((data) => {
        const value = String(data.settings?.surface_navigation_order ?? "destination_then_previous");
        if (value === "previous_then_destination" || value === "destination_then_previous") setSurfaceNavigationOrder(value);
      })
      .catch(() => {
        // Navigation still works with the default order when preferences are unavailable.
      });
  }, [scope]);

  useEffect(() => {
    const surfaceSections: SectionKey[] = ["chat", "calendar", "files"];
    if (!surfaceSections.includes(activeSection)) return;
    let previous = previousSurfaceRef.current;
    if (!previous) {
      try {
        const saved = JSON.parse(window.localStorage.getItem(`hub:active-surface:${scope}`) ?? "null") as { section?: SectionKey; route?: string } | null;
        if (saved?.section && saved.route) previous = { section: saved.section, route: saved.route };
      } catch {
        // Local route history is optional.
      }
    }
    if (previous && previous.section !== activeSection) {
      setPreviousSurfaceRoute(previous);
      try {
        window.localStorage.setItem(`hub:previous-surface:${scope}`, JSON.stringify(previous));
      } catch {
        // Local route history is optional.
      }
    }
    previousSurfaceRef.current = { section: activeSection, route: currentRoute };
    try {
      window.localStorage.setItem(`hub:active-surface:${scope}`, JSON.stringify({ section: activeSection, route: currentRoute }));
    } catch {
      // Local route history is optional.
    }
  }, [activeSection, currentRoute, scope]);

  useEffect(() => {
    try {
      const saved = JSON.parse(window.localStorage.getItem(`hub:previous-surface:${scope}`) ?? "null") as { section?: SectionKey; route?: string } | null;
      if (saved?.route && saved?.section) setPreviousSurfaceRoute({ section: saved.section, route: saved.route });
    } catch {
      // Local route history is optional.
    }
  }, [scope]);

  useEffect(() => {
    if (!activeSection) return;
    const isSurface = ["chat", "calendar", "files"].includes(activeSection);
    const hasSurfaceContext = Boolean(searchParams.get("channel")) ||
      /^\/app\/[^/]+\/entities\/[^/]+/.test(pathname);
    const isPreviousSurfaceFallback = searchParams.get("selection") === "previous";
    // Keep the last channel/detail route for each surface. A bare landing
    // route must not erase it, otherwise switching away and back loses the
    // user's previous channel.
    // A route created only to show the previous surface's channel is also
    // transient; otherwise it would overwrite the destination's real
    // remembered route before the second navigation click can restore it.
    if (isSurface && (!hasSurfaceContext || isPreviousSurfaceFallback)) return;
    setLastRoutes((previous) => {
      let stored = previous;
      try {
        const persisted = JSON.parse(window.localStorage.getItem("hub:last-routes") ?? "{}") as Record<string, string>;
        if (persisted && typeof persisted === "object") stored = { ...previous, ...persisted };
      } catch {
        // Local route history is optional.
      }
      const next = { ...stored, [routeKey]: currentRoute };
      try {
        window.localStorage.setItem("hub:last-routes", JSON.stringify(next));
      } catch {
        // Local route history is optional.
      }
      return next;
    });
  }, [activeSection, currentRoute, pathname, routeKey, searchParams]);

  // Keep the top of every newly opened route below the sticky header. This is
  // especially important when a route is changed while the previous page was
  // scrolled down and the browser preserves its scroll position.
  useEffect(() => {
    window.requestAnimationFrame(() =>
      window.scrollTo({ top: 0, left: 0, behavior: "auto" }),
    );
  }, [currentRoute]);

  const searchParamRecord = useMemo(() => {
    const out: Record<string, string> = {};
    searchParams.forEach((value, key) => {
      out[key] = value;
    });
    return out;
  }, [searchParams]);

  const filteredSearchResults = useMemo(() => {
    const category = searchCategories.find(
      (item) => item.key === searchCategory,
    );
    if (!category || category.key === "all") return searchResults;
    return searchResults.filter((resultItem) =>
      category.types.some((type) => type === resultItem.type),
    );
  }, [searchCategory, searchResults]);

  const visibleNotifications = useMemo(() => {
    if (user.role === "admin") return notifications.events;
    return notifications.events.filter(
      (event) => !String(event.event_type ?? "").includes("sync"),
    );
  }, [notifications.events, user.role]);

  const sectionLabels: Record<SectionKey, string> = en
    ? {
        home: "Home",
        chat: "Chat",
        calendar: "Calendar",
        files: "Files",
        me: "My Page",
      }
    : {
        home: "ホーム",
        chat: "チャット",
        calendar: "予定",
        files: "ファイル",
        me: "マイページ",
      };
  const searchCategoryLabels: Record<SearchCategory, string> = en
    ? {
        all: "All",
        chat: "Chat",
        calendar: "Calendar / Tasks",
        files: "Files",
        member: "Members",
        notification: "Notifications",
        settings: "Settings",
        ai: "AI history",
      }
    : {
        all: "すべて",
        chat: "チャット",
        calendar: "予定・記録",
        files: "ファイル",
        member: "メンバー",
        notification: "通知",
        settings: "設定",
        ai: "AI履歴",
      };
  const ui = en
    ? {
        search: "Search",
        askAi: "Ask AI",
        searchPlaceholder: "Search across the workspace",
        noResults: "No results yet",
        notifications: "Notifications",
        markRead: "Mark all read",
        noNotifications: "No new notifications",
        allScope: "All",
        globalLabel: "global",
        globalBadge: "ALL",
        switchWorkspace: "Switch workspace",
        mainNav: "Main navigation",
        mobileNav: "Mobile navigation",
      }
    : {
        search: "検索",
        askAi: "AIに質問",
        searchPlaceholder: "横断検索",
        noResults: "このカテゴリの結果はまだありません",
        notifications: "通知",
        markRead: "既読にする",
        noNotifications: "新しい通知はありません",
        allScope: "全体",
        globalLabel: "global",
        globalBadge: "global",
        switchWorkspace: "ワークスペースを切り替える",
        mainNav: "主要ナビゲーション",
        mobileNav: "モバイル主要ナビゲーション",
      };

  Object.assign(sectionLabels, {
    home: t("home"),
    chat: t("chat"),
    calendar: t("calendar"),
    files: t("files"),
    me: t("myPage"),
  });
  Object.assign(searchCategoryLabels, {
    all: t("all"),
    chat: t("chat"),
    calendar: t("calendarTasks"),
    files: t("files"),
    member: t("members"),
    notification: t("notifications"),
    settings: t("settings"),
    ai: t("aiHistory"),
  });
  Object.assign(ui, {
    search: t("search"),
    askAi: t("askAi"),
    searchPlaceholder: t("searchWorkspace"),
    noResults: t("noSearchResults"),
    notifications: t("notifications"),
    markRead: t("markAllRead"),
    noNotifications: t("noNotifications"),
    allScope: t("all"),
  });

  useEffect(() => {
    if (!showSearch || searchMode !== "search") return;
    searchInputRef.current?.focus();
  }, [searchMode, showSearch]);

  useEffect(() => {
    if (!showSearch || searchMode !== "search") return;
    let alive = true;
    const timer = window.setTimeout(
      () => {
        setSearchLoading(true);
        setSearchError(false);
        fetchJson<{ results: SearchResult[] }>(
          `/api/app-search?scope=${encodeURIComponent(scope)}&q=${encodeURIComponent(searchQuery.trim())}`,
        )
          .then((data) => {
            if (alive) {
              setSearchResults(data.results ?? []);
              setSearchLoading(false);
            }
          })
          .catch(() => {
            if (alive) {
              setSearchResults([]);
              setSearchLoading(false);
              setSearchError(true);
            }
          });
      },
      searchQuery.trim() ? 180 : 0,
    );
    return () => {
      alive = false;
      window.clearTimeout(timer);
    };
  }, [scope, searchMode, searchQuery, showSearch]);

  useEffect(() => {
    let alive = true;
    // The tray kept showing stale data until a reload, so refresh on an
    // interval, on window focus, and whenever the popover is opened.
    function load() {
      fetchJson<{ unread: number; events: NotificationEvent[] }>(
        "/api/notifications",
        undefined,
        { silent: true },
      )
        .then((data) => {
          if (!alive) return;
          const events = data.events ?? [];
          setNotifications({
            unread: data.unread ?? 0,
            events,
          });
          // Chime (and, in the background, an OS notification) for new events.
          const newest = events.reduce((max, event) => Math.max(max, Number(event.id) || 0), 0);
          const seen = lastNotificationId.current;
          lastNotificationId.current = Math.max(seen ?? 0, newest);
          if (seen == null || newest <= seen) return;
          const arriving = events.filter((event) => (Number(event.id) || 0) > seen);
          // 画面を見ているときだけチャイムを鳴らす（裏ではPushのOS通知が担当する）。
          if (!document.hidden) playNotificationChime();
          const latest = arriving.reduce<NotificationEvent | null>((newest, event) => !newest || (Number(event.id) || 0) > (Number(newest.id) || 0) ? event : newest, null);
          if (latest) {
            // 通知トレイを見ていないときだけ、右上に小さく知らせる。
            showNotificationToast({
              id: latest.id,
              title: String(latest.title ?? translateEventType(latest.event_type, prefs.locale) ?? t("notifications")),
              body: String(latest.body ?? latest.workspace_name ?? ""),
              url: String(latest.url ?? `/app/${scope}/home`),
            });
          }
          // Push購読が無い端末だけ、画面側でOS通知を代行する（購読済みは
          // Service Worker が通知するため、ここで出すと二重になる）。
          if (document.hidden && !pushActiveRef.current) {
            const first = arriving[0];
            if (first) showSystemNotification(String(first.title ?? "通知"), String(first.body ?? ""), first.url ? String(first.url) : undefined);
          }
        })
        .catch(() => undefined);
    }
    load();
    const timer = window.setInterval(load, 20_000);
    function onVisibility() {
      if (document.visibilityState === "visible") load();
    }
    window.addEventListener("focus", load);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      alive = false;
      window.clearInterval(timer);
      window.removeEventListener("focus", load);
      document.removeEventListener("visibilitychange", onVisibility);
    };
    // Locale/copy lookups used for the toast stay stable for the session.
    // Re-fetching on every navigation only added a request and a shell
    // re-render while the destination was loading; the interval/focus/visible
    // refreshes keep it current.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- polling effect
  }, [showNotifications]);

  useEffect(() => {
    // Browsers only allow audio after a gesture, so unlock on the first one.
    function unlock() {
      primeNotificationSound();
      window.removeEventListener("pointerdown", unlock);
      window.removeEventListener("keydown", unlock);
    }
    window.addEventListener("pointerdown", unlock, { once: true });
    window.addEventListener("keydown", unlock, { once: true });
    return () => {
      window.removeEventListener("pointerdown", unlock);
      window.removeEventListener("keydown", unlock);
    };
  }, []);

  useEffect(() => {
    // Screen-top system messages are kept locally and listed in the tray.
    function sync() {
      setSystemMessages(listSystemMessages());
      setUnseenSystemMessages(countUnseenSystemMessages());
    }
    sync();
    window.addEventListener(SYSTEM_MESSAGE_EVENT, sync);
    return () => window.removeEventListener(SYSTEM_MESSAGE_EVENT, sync);
  }, []);

  useEffect(() => {
    // Whether this device already receives OS notifications through Web Push.
    void currentPushSubscription().then((subscription) => { pushActiveRef.current = Boolean(subscription); }).catch(() => undefined);
  }, []);

  useEffect(() => {
    if (!showNotifications) return;
    setSystemMessages(listSystemMessages());
    markSystemMessagesSeen();
    setUnseenSystemMessages(0);
  }, [showNotifications]);

  useEffect(() => {
    function onPointerDown(event: PointerEvent) {
      const target = event.target as Node;
      if (
        workspaceMenuOpen &&
        workspaceMenuRef.current &&
        !workspaceMenuRef.current.contains(target)
      )
        setWorkspaceMenuOpen(false);
      if (
        showNotifications &&
        notificationRef.current &&
        !notificationRef.current.contains(target)
      )
        setShowNotifications(false);
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setWorkspaceMenuOpen(false);
        setShowNotifications(false);
        setShowSearch(false);
      }
    }
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [showNotifications, workspaceMenuOpen]);

  function switchScope(nextScope: string) {
    setWorkspaceMenuOpen(false);
    router.push(
      lastRoutes[`${nextScope}:${activeSection}`] ||
        `/app/${nextScope}/${activeSection}`,
    );
  }

  function openResult(resultItem: SearchResult) {
    setShowSearch(false);
    router.push(resultItem.href);
  }

  function onSearchInputKeyDown(event: ReactKeyboardEvent<HTMLInputElement>) {
    if (event.key === "Enter" && filteredSearchResults[0]) {
      event.preventDefault();
      openResult(filteredSearchResults[0]);
    }
  }

  async function markAllNotificationsRead() {
    const data = await fetchJson<{
      unread: number;
      events: NotificationEvent[];
    }>("/api/notifications", {
      method: "PATCH",
      body: JSON.stringify({ ids: [] }),
    });
    setNotifications({ unread: data.unread ?? 0, events: data.events ?? [] });
  }

  return (
    <div
      data-testid="unified-shell"
      className="flex h-dvh min-h-0 flex-col overflow-hidden bg-grid text-[color:var(--fg)]"
    >
      <header
        data-testid="unified-top-shell"
        className="relative z-[100] flex-none border-b border-[color:var(--line)] bg-white/95 backdrop-blur"
      >
        <div className="device-shell-header-row mx-auto flex h-12 max-w-[1520px] items-center justify-between gap-3 px-4 sm:h-16 sm:grid sm:grid-cols-[minmax(0,auto)_minmax(0,1fr)_auto] sm:px-6">
          <details
            ref={workspaceMenuRef}
            className="device-header-workspace relative min-w-0"
            open={workspaceMenuOpen}
            onToggle={(event) => {
              setWorkspaceMenuOpen(event.currentTarget.open);
            }}
          >
            <summary
              data-testid="workspace-selector"
              className={clsx(
                "device-workspace-trigger flex h-11 max-w-[52vw] items-center gap-1.5 text-left text-base font-semibold outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent-strong)] sm:max-w-[320px]",
                isGlobalScope
                  ? "min-w-[9.25rem] rounded-full border border-[color:var(--accent-strong)] bg-[color:var(--accent-soft)] px-4 text-[color:var(--accent-strong)] shadow-sm"
                  : "text-[color:var(--fg)] hover:text-[color:var(--accent-strong)]",
              )}
              aria-label={ui.switchWorkspace}
              aria-haspopup="listbox"
              aria-expanded={workspaceMenuOpen}
              onKeyDown={(event) => {
                if (event.key === "ArrowDown") setWorkspaceMenuOpen(true);
              }}
            >
              {currentWorkspace?.icon_url ? (
                // eslint-disable-next-line @next/next/no-img-element
                <img
                  src={currentWorkspace.icon_url}
                  alt=""
                  className="h-6 w-6 shrink-0 rounded-md object-cover"
                />
              ) : null}
              <span>{workspaceLabel}</span>
              <ChevronDownIcon className="h-4 w-4 shrink-0" />
            </summary>
            {workspaceMenuOpen ? (
              <div
                data-testid="workspace-switcher-menu"
                role="listbox"
                className="absolute left-0 top-full z-[60] mt-1 grid max-h-[70vh] min-w-[320px] w-[min(360px,calc(100vw-2rem))] gap-1 overflow-y-auto overscroll-contain rounded-md border border-[color:var(--line)] bg-white p-2 shadow-lg"
              >
                <button
                  data-testid="workspace-option-all"
                  type="button"
                  role="option"
                  aria-selected={scope === "all"}
                  className={workspaceOptionClass(scope === "all", true)}
                  onClick={() => switchScope("all")}
                >
                  <span className="inline-flex items-center gap-2">
                    <span
                      className="inline-block h-2.5 w-2.5 rounded-full bg-[color:var(--accent-strong)]"
                      aria-hidden="true"
                    />
                    <span className="font-semibold">{ui.allScope}</span>
                  </span>
                  <span className="rounded-full border border-[color:var(--accent-strong)] bg-white px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-[color:var(--accent-strong)]">
                    {ui.globalBadge}
                  </span>
                </button>
                {workspaces.map((workspace) => (
                  <button
                    data-testid={`workspace-option-${workspace.slug}`}
                    key={workspace.slug}
                    type="button"
                    role="option"
                    aria-selected={scope === workspace.slug}
                    className={workspaceOptionClass(scope === workspace.slug)}
                    onClick={() => switchScope(workspace.slug)}
                  >
                    <span className="inline-flex min-w-0 items-center gap-2">
                      {workspace.icon_url ? (
                        // eslint-disable-next-line @next/next/no-img-element
                        <img
                          src={workspace.icon_url}
                          alt=""
                          className="h-7 w-7 shrink-0 rounded-md object-cover"
                        />
                      ) : null}
                      <span className="truncate">{workspace.name}</span>
                    </span>
                    <span className="text-xs text-[color:var(--muted)]">
                      {workspace.slug}
                    </span>
                  </button>
                ))}
              </div>
            ) : null}
          </details>

          <nav
            data-testid="unified-main-nav"
            className="device-header-nav device-desktop-main-nav hidden items-center justify-center gap-2 sm:flex"
            aria-label={ui.mainNav}
          >
            {sections.map((section) => (
              <ShellNavLink
                key={section.key}
                section={section}
                scope={scope}
                active={activeSection === section.key}
                user={user}
                variant="desktop"
                label={sectionLabels[section.key]}
                href={lastRoutes[`${scope}:${section.key}`]}
                previousSurfaceRoute={previousSurfaceRoute}
                selectedCalendarEntity={selectedCalendarEntity}
                currentSurfaceRoute={activeSection !== "home" && activeSection !== "me" ? { section: activeSection, route: currentRoute } : null}
                surfaceNavigationOrder={surfaceNavigationOrder}
                onBeforeNavigate={() => {
                  setShowSearch(false);
                  setShowNotifications(false);
                  setWorkspaceMenuOpen(false);
                }}
              />
            ))}
          </nav>

          <div className="device-header-actions flex items-center justify-end gap-1.5">
            <IconButton
              testId="global-search-button"
              label={t("searchOrAskAi")}
              displayLabel={t("search")}
              onClick={() => setShowSearch((current) => !current)}
            >
              <SearchIcon className="h-6 w-6" />
            </IconButton>
            {user.role === "admin" ? (
              <Link
                href="/admin/users"
                className="hidden h-9 items-center rounded-md border border-[color:var(--line)] px-2 text-xs font-semibold sm:inline-flex"
              >
                {t("admin")}
              </Link>
            ) : null}
            <div ref={notificationRef} className="relative">
              <IconButton
                testId="notification-bell"
                label={ui.notifications}
                displayLabel={en ? "Notifications" : "通知"}
                onClick={() => setShowNotifications((current) => !current)}
              >
                <NotificationIcon className="h-6 w-6" />
                {notifications.unread + unseenSystemMessages ? (
                  <span
                    data-testid="notification-unread-count"
                    className="absolute right-1 top-1 min-w-4 rounded-full bg-[color:var(--accent-strong)] px-1 text-[10px] leading-4 text-[color:#fff]"
                  >
                    {notifications.unread + unseenSystemMessages}
                  </span>
                ) : null}
              </IconButton>
              {showNotifications ? (
                <div
                  data-testid="notification-popover"
                  className="fixed inset-x-3 top-16 z-50 max-h-[calc(100dvh-5rem)] overflow-y-auto rounded-md border border-[color:var(--line)] bg-white p-2 shadow-lg md:absolute md:inset-auto md:right-0 md:top-auto md:mt-2 md:w-[420px]"
                >
                  <div className="flex items-center justify-between gap-3 px-2 py-2">
                    <span className="text-sm font-semibold">
                      {en ? "Notification tray" : "通知トレイ"}
                    </span>
                    <button
                      type="button"
                      className="button-secondary h-8 rounded-md px-2 text-xs"
                      onClick={() => void markAllNotificationsRead()}
                    >
                      {ui.markRead}
                    </button>
                  </div>
                  <div className="mb-2 flex items-center justify-between gap-2 px-2" role="tablist" aria-label={en ? "Notification kinds" : "通知の種類"}>
                    <span className="flex gap-1">
                      {([["events", en ? "Messages" : "通常メッセージ"], ["system", en ? "System messages" : "システムメッセージ"]] as const).map(([tab, label]) => (
                        <button
                          key={tab}
                          type="button"
                          role="tab"
                          aria-selected={notificationTab === tab}
                          data-testid={`notification-tab-${tab}`}
                          className={`h-8 rounded-md px-3 text-xs font-semibold ${notificationTab === tab ? "bg-[color:var(--accent-soft)] text-[color:var(--accent-strong)]" : "border border-[color:var(--line)] text-[color:var(--muted)]"}`}
                          onClick={() => setNotificationTab(tab)}
                        >
                          {label}{tab === "system" && systemMessages.length ? `（${systemMessages.length}）` : ""}
                        </button>
                      ))}
                    </span>
                    {notificationTab === "system" && systemMessages.length ? (
                      <button type="button" data-testid="notification-system-clear" className="h-7 shrink-0 rounded-md border border-[color:var(--line)] px-2 text-[11px] text-[color:var(--muted)]" onClick={() => clearSystemMessages()}>
                        {en ? "Clear" : "消去"}
                      </button>
                    ) : null}
                  </div>
                  {notificationTab === "events" ? visibleNotifications.slice(0, 30).map((event) => (
                    <Link
                      key={String(event.id)}
                      href={String(event.url ?? `/app/${scope}/home`)}
                      className="block rounded-md px-3 py-2 hover:bg-[color:var(--bg-subtle)]"
                      onClick={() => setShowNotifications(false)}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <span className="truncate text-sm font-medium">
                          {String(
                            event.title ??
                              (translateEventType(
                                event.event_type,
                                prefs.locale,
                              ) ||
                                t("notifications")),
                          )}
                        </span>
                        <span className="flex shrink-0 items-center gap-2">
                          {event.created_at ? (
                            <time className="text-[11px] text-[color:var(--muted)]">
                              {formatNotificationTime(event.created_at)}
                            </time>
                          ) : null}
                          {!event.read_at ? (
                            <span
                              className="h-2 w-2 rounded-full bg-[color:var(--accent-strong)]"
                              aria-label={t("unread")}
                            />
                          ) : null}
                        </span>
                      </div>
                      <div className="mt-1 line-clamp-2 text-xs text-[color:var(--muted)]">
                        {String(event.body ?? event.workspace_name ?? "")}
                      </div>
                    </Link>
                  )) : null}
                  {notificationTab === "system" ? systemMessages.map((entry) => (
                    <div key={entry.id} className="rounded-md px-3 py-2" data-testid="notification-system-message">
                      <div className="flex items-start justify-between gap-3">
                        <span className={`min-w-0 text-sm ${entry.kind === "error" ? "text-[color:var(--danger)]" : "font-medium"}`}>{entry.message}</span>
                        <time className="shrink-0 text-[11px] text-[color:var(--muted)]">{formatNotificationTime(entry.at)}</time>
                      </div>
                    </div>
                  )) : null}
                  {(notificationTab === "events" ? visibleNotifications.length : systemMessages.length) === 0 ? (
                    <div className="px-3 py-5 text-sm text-[color:var(--muted)]">
                      {ui.noNotifications}
                    </div>
                  ) : null}
                  <div className="mt-1 border-t border-[color:var(--line)] px-3 py-2">
                    <Link
                      href={`/app/${encodeURIComponent(scope)}/me?section=my-notifications`}
                      data-testid="notification-settings-link"
                      className="text-xs font-semibold text-[color:var(--accent-strong)]"
                      onClick={() => setShowNotifications(false)}
                    >
                      {en ? "Notification settings (allow on this device)" : "通知設定（この端末で受け取る）"}
                    </Link>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </header>

      <NotificationPermissionPrompt workspaceSlug={isGlobalScope ? undefined : scope} />
      <main
        data-testid="unified-main"
        className={clsx(
          "device-shell-main min-h-0 flex-1 overscroll-contain w-full max-w-none px-0 pb-0 pt-0 md:pb-0 md:pt-0",
          activeSection === "chat"
            ? "device-shell-main-chat overflow-hidden"
            : "overflow-y-auto",
        )}
      >
        {children}
        {activeSection === "chat" ? null : (
          <div aria-hidden="true" className="device-mobile-bottom-spacer h-[calc(env(safe-area-inset-bottom)+6rem)] shrink-0 sm:hidden" />
        )}
      </main>

      <nav
        data-testid="unified-mobile-bottom-nav"
        className="device-mobile-bottom-nav z-40 sm:hidden"
        aria-label={ui.mobileNav}
      >
        <div
          className={clsx(
            "grid h-full w-full items-stretch gap-0.5",
            user.role === "admin" ? "grid-cols-1" : "grid-cols-5",
          )}
        >
          {(user.role === "admin" ? adminMobileSections : mobileSections).map(
            (key) => {
              if (key === "admin") {
                return (
                  <Link
                    key={key}
                    href="/admin/users"
                    className="flex min-h-12 items-center justify-center rounded-md bg-[color:var(--accent-soft)] text-sm font-semibold text-[color:var(--accent-strong)]"
                  >
                    {t("adminMenu")}
                  </Link>
                );
              }
              const section = sections.find((item) => item.key === key)!;
              return (
                <ShellNavLink
                  key={section.key}
                  section={section}
                  scope={scope}
                  active={activeSection === section.key}
                  user={user}
                  variant="mobile"
                  label={sectionLabels[section.key]}
                  href={lastRoutes[`${scope}:${section.key}`]}
                  previousSurfaceRoute={previousSurfaceRoute}
                  selectedCalendarEntity={selectedCalendarEntity}
                  currentSurfaceRoute={activeSection !== "home" && activeSection !== "me" ? { section: activeSection, route: currentRoute } : null}
                  surfaceNavigationOrder={surfaceNavigationOrder}
                  onBeforeNavigate={() => {
                    setShowSearch(false);
                    setShowNotifications(false);
                    setWorkspaceMenuOpen(false);
                  }}
                />
              );
            },
          )}
        </div>
      </nav>

      {showSearch ? (
        <div
          data-testid="global-search-modal"
          role="presentation"
          className="fixed inset-0 z-50 bg-black/20 p-3 backdrop-blur-sm md:grid md:place-items-start md:pt-[12vh]"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setShowSearch(false);
          }}
        >
          <section
            role="dialog"
            aria-modal="true"
            aria-labelledby="global-search-title"
            className="mx-auto flex h-[calc(100dvh-1.5rem)] w-full max-w-2xl flex-col overflow-hidden rounded-md border border-[color:var(--line)] bg-white shadow-[0_24px_80px_rgba(18,18,18,0.22)] md:h-[min(680px,76dvh)]"
          >
            <div className="border-b border-[color:var(--line)] p-3">
              <div className="mb-2 flex items-center justify-between gap-3">
                <h2 id="global-search-title" className="text-sm font-semibold">
                  {ui.search}
                </h2>
                <button
                  type="button"
                  className="icon-button h-9 w-9 rounded-md"
                  aria-label={en ? "Close search" : "検索を閉じる"}
                  onClick={() => setShowSearch(false)}
                >
                  ×
                </button>
              </div>
              <div
                className="grid grid-cols-2 rounded-md bg-[color:var(--bg-subtle)] p-1"
                role="tablist"
                aria-label={en ? "Search mode" : "検索モード"}
              >
                <button
                  data-testid="global-search-mode"
                  role="tab"
                  aria-selected={searchMode === "search"}
                  type="button"
                  className={clsx(
                    "h-9 rounded-md text-sm font-semibold",
                    searchMode === "search"
                      ? "bg-white shadow-sm"
                      : "text-[color:var(--muted)]",
                  )}
                  onClick={() => setSearchMode("search")}
                >
                  <SearchIcon className="mr-1 inline h-4 w-4" />
                  {ui.search}
                </button>
                <button
                  data-testid="global-ai-mode"
                  role="tab"
                  aria-selected={searchMode === "ai"}
                  type="button"
                  className={clsx(
                    "h-9 rounded-md text-sm font-semibold",
                    searchMode === "ai"
                      ? "bg-white shadow-sm"
                      : "text-[color:var(--muted)]",
                  )}
                  onClick={() => setSearchMode("ai")}
                >
                  <AiSearchIcon className="mr-1 inline h-5 w-5" />
                  {ui.askAi}
                </button>
              </div>
              {searchMode === "search" ? (
                <>
                  <label className="mt-3 flex h-12 items-center gap-3 rounded-md border border-[color:var(--line)] px-3 focus-within:ring-2 focus-within:ring-[color:var(--accent-strong)]">
                    <SearchIcon className="h-5 w-5 shrink-0 text-[color:var(--muted)]" />
                    <input
                      ref={searchInputRef}
                      data-testid="global-search-input"
                      className="h-full min-w-0 flex-1 bg-transparent text-base outline-none"
                      value={searchQuery}
                      onChange={(event) => setSearchQuery(event.target.value)}
                      onKeyDown={onSearchInputKeyDown}
                      placeholder={ui.searchPlaceholder}
                    />
                  </label>
                  <div className="mt-3 flex gap-1 overflow-x-auto pb-1">
                    {searchCategories.map((category) => (
                      <button
                        data-testid={`search-category-${category.key}`}
                        key={category.key}
                        type="button"
                        className={clsx(
                          "h-8 shrink-0 rounded-md border px-2.5 text-xs font-semibold",
                          searchCategory === category.key
                            ? "border-[color:var(--accent-strong)] bg-[color:var(--accent-soft)] text-[color:var(--accent-strong)]"
                            : "border-[color:var(--line)] bg-white text-[color:var(--muted)]",
                        )}
                        onClick={() => setSearchCategory(category.key)}
                      >
                        {searchCategoryLabels[category.key]}
                      </button>
                    ))}
                  </div>
                </>
              ) : null}
            </div>
            {searchMode === "search" ? (
              <div
                data-testid="global-search-results"
                aria-busy={searchLoading}
                className="min-h-0 flex-1 overflow-y-auto p-2"
              >
                {searchLoading ? (
                  <div
                    role="status"
                    className="px-3 py-2 text-sm text-[color:var(--muted)]"
                  >
                    {en ? "Searching…" : "検索中…"}
                  </div>
                ) : null}
                {searchError ? (
                  <div
                    role="alert"
                    className="mx-2 mb-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800"
                  >
                    {en
                      ? "Search failed. Please try again."
                      : "検索に失敗しました。もう一度お試しください。"}
                  </div>
                ) : null}
                {filteredSearchResults.map((resultItem) => (
                  <button
                    data-testid="global-search-result"
                    key={resultItem.id}
                    type="button"
                    className="block w-full rounded-md px-3 py-3 text-left hover:bg-[color:var(--bg-subtle)]"
                    onClick={() => openResult(resultItem)}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <span className="truncate text-sm font-semibold">
                        {resultItem.label}
                      </span>
                      <span className="shrink-0 rounded border border-[color:var(--line)] px-1.5 py-0.5 text-[11px] text-[color:var(--muted)]">
                        {resultItem.type}
                      </span>
                    </div>
                    <div className="mt-1 truncate text-xs text-[color:var(--muted)]">
                      {resultItem.detail}
                    </div>
                  </button>
                ))}
                {filteredSearchResults.length === 0 ? (
                  <div className="grid min-h-40 place-items-center rounded-md border border-dashed border-[color:var(--line)] px-4 text-center text-sm text-[color:var(--muted)]">
                    {ui.noResults}
                  </div>
                ) : null}
              </div>
            ) : (
              <div className="min-h-0 flex-1">
                <AiFloatingAssistant
                  mode="embedded"
                  workspaceSlug={
                    scope === "all" ? null : (currentWorkspace?.slug ?? null)
                  }
                  workspaceOptions={workspaces}
                  context={{
                    scope,
                    section: activeSection,
                    pathname,
                    searchParams: searchParamRecord,
                    selectedWorkspace,
                    user,
                  }}
                />
              </div>
            )}
          </section>
        </div>
      ) : null}
    </div>
  );
}

function ShellNavLink({
  section,
  scope,
  active,
  user,
  variant,
  label,
  href,
  previousSurfaceRoute,
  selectedCalendarEntity,
  currentSurfaceRoute,
  surfaceNavigationOrder,
  onBeforeNavigate,
}: {
  section: (typeof sections)[number];
  scope: string;
  active: boolean;
  user: ShellUser;
  variant: "desktop" | "mobile";
  label: string;
  href?: string;
  previousSurfaceRoute?: { section: SectionKey; route: string } | null;
  selectedCalendarEntity?: string | null;
  currentSurfaceRoute?: { section: SectionKey; route: string } | null;
  surfaceNavigationOrder: "destination_then_previous" | "previous_then_destination";
  onBeforeNavigate?: () => void;
}) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const Icon = section.Icon;
  const isMe = section.key === "me";
  const mobile = variant === "mobile";
  // Prefer the last contextual route recorded for each surface. This keeps
  // the last channel for that surface independent from the current surface.
  const rememberedHref = href;
  const sourceSurfaceRoute = currentSurfaceRoute ?? previousSurfaceRoute;
  const previousSurfaceHref = !active && sourceSurfaceRoute
    ? surfaceRouteForSection(scope, section.key, sourceSurfaceRoute.route, sourceSurfaceRoute.section, selectedCalendarEntity)
    : null;
  // When moving between surfaces, both candidates are meaningful: the
  // destination's remembered route and the route for the channel that was
  // active on the surface we just left. The preference controls only which
  // one wins; the other remains a fallback. For the already-active surface,
  // keep its remembered route as the stable same-surface target.
  const preferredHref = active
    ? rememberedHref
    : surfaceNavigationOrder === "previous_then_destination"
      ? previousSurfaceHref || rememberedHref
      : rememberedHref || previousSurfaceHref;
  const targetHref = preferredHref || rememberedHref || previousSurfaceHref || contextualNavHref({
        scope,
        section: section.key,
        pathname,
        searchParams,
        fallback: href || topLevelSectionHref(scope, section.key),
      });
  const currentHref = searchParams.toString()
    ? `${pathname}?${searchParams.toString()}`
    : pathname;
  return (
    <Link
      data-testid={
        variant === "desktop"
          ? `nav-${section.key}`
          : `mobile-nav-${section.key}`
      }
      href={targetHref}
      aria-label={label}
      aria-current={active ? "page" : undefined}
      onClick={(event) => {
        onBeforeNavigate?.();
        // Do not reload the document when the active destination is already
        // open. A hard navigation here caused the mobile chat view to flash
        // through several loading states before settling.
        if (active && targetHref === currentHref) {
          event.preventDefault();
          return;
        }
        window.scrollTo({ top: 0, left: 0, behavior: "auto" });
        document
          .querySelector<HTMLElement>('[data-testid="unified-main"]')
          ?.scrollTo({ top: 0, left: 0, behavior: "auto" });
      }}
      className={clsx(
        "group relative outline-none transition focus-visible:ring-2 focus-visible:ring-[color:var(--accent-strong)]",
        mobile
          ? "flex min-h-11 flex-col items-center justify-center gap-0.5 rounded-full px-0.5 py-1 text-[10px] font-medium motion-reduce:transition-none"
          : "grid h-14 min-w-[4.25rem] place-items-center rounded-md px-1 py-1 text-[11px]",
        active ? "text-[color:var(--accent-strong)]" : "text-[color:var(--muted)]",
        mobile && active
          ? "bg-[color:var(--accent-strong)]/12 ring-1 ring-inset ring-[color:var(--accent-strong)]/20 shadow-[0_1px_3px_rgba(18,18,18,0.10)] backdrop-blur-sm"
          : "hover:bg-[color:var(--bg-subtle)]",
      )}
    >
      <span
        className={clsx(
          "grid place-items-center transition",
          mobile
            ? ""
            : clsx(
                "h-9 w-9 rounded-md border",
                active
                  ? "border-[color:var(--accent-strong)] bg-[color:var(--accent-soft)]"
                  : "border-transparent bg-transparent",
              ),
        )}
      >
        {isMe && mobile && user.avatarUrl ? (
          <Image
            src={user.avatarUrl}
            alt=""
            width={36}
            height={36}
            className={clsx("rounded-full object-cover", mobile ? "h-5 w-5" : "h-8 w-8")}
            unoptimized
          />
        ) : (
          <Icon
            className={clsx(mobile ? "h-5 w-5" : "h-5 w-5")}
            strokeWidth={mobile && active ? 2.4 : 2}
          />
        )}
      </span>
      {mobile ? (
        <span
          className={clsx(
            "mt-0.5 max-w-full truncate",
            active && "font-semibold text-[color:var(--accent-strong)]",
          )}
        >
          {label}
        </span>
      ) : (
        <span
          className={clsx(
            "mt-0.5 whitespace-nowrap text-[11px]",
            active && "font-semibold text-[color:var(--accent-strong)]",
          )}
        >
          {label}
        </span>
      )}
    </Link>
  );
}

function IconButton({
  children,
  label,
  displayLabel,
  onClick,
  testId,
}: {
  children: ReactNode;
  label: string;
  displayLabel: string;
  onClick: () => void;
  testId: string;
}) {
  return (
    <button
      data-testid={testId}
      type="button"
      className="device-header-action-button relative grid h-14 min-w-14 place-items-center rounded-md px-1 text-[color:var(--muted)] outline-none hover:bg-[color:var(--bg-subtle)] focus-visible:ring-2 focus-visible:ring-[color:var(--accent-strong)]"
      onClick={onClick}
      aria-label={label}
      title={label}
    >
      <span className="grid place-items-center">{children}</span>
      <span className="device-header-action-label mt-0.5 text-[11px] leading-none">
        {displayLabel}
      </span>
    </button>
  );
}

function workspaceOptionClass(active: boolean, global = false) {
  return clsx(
    "grid min-h-11 grid-cols-[1fr_auto] items-center gap-3 rounded-md px-3 py-2 text-left text-sm outline-none hover:bg-[color:var(--bg-subtle)] focus-visible:ring-2 focus-visible:ring-[color:var(--accent-strong)]",
    active &&
      (global
        ? "border border-[color:var(--accent-strong)] bg-[linear-gradient(180deg,rgba(185,28,28,0.12),rgba(185,28,28,0.04))] text-[color:var(--accent-strong)] shadow-sm"
        : "bg-[color:var(--accent-soft)] text-[color:var(--accent-strong)]"),
  );
}

function getActiveSection(
  pathname: string,
  searchParams?: URLSearchParams,
): SectionKey {
  if (pathname.startsWith("/w/") && pathname.includes("/settings")) return "me";
  if (/^\/app\/[^/]+\/entities\/?$/.test(pathname)) {
    const surface = searchParams?.get("surface");
    if (surface === "chat" || surface === "calendar" || surface === "files") {
      return surface;
    }
    // The chat surface intentionally uses the canonical, queryless
    // `/entities` URL. Keep the shell highlight aligned with that route;
    // calendar and files use an explicit surface query.
    return "chat";
  }
  if (/^\/app\/[^/]+\/entities\/[^/]+\/manage/.test(pathname)) return "me";
  if (/^\/app\/[^/]+\/entities\/[^/]+/.test(pathname)) {
    const view = searchParams?.get("view");
    if (view === "file" || view === "document") return "files";
    if (view === "calendar" || view === "task" || view === "attendance")
      return "calendar";
    return "chat";
  }
  const candidate = pathname.split("/")[3] as SectionKey | undefined;
  return sections.some((section) => section.key === candidate)
    ? (candidate as SectionKey)
    : "home";
}

function contextualNavHref({
  scope,
  section,
  pathname,
  searchParams,
  fallback,
}: {
  scope: string;
  section: SectionKey;
  pathname: string;
  searchParams: URLSearchParams;
  fallback: string;
}) {
  const root = `/app/${scope}`;
  // Home is the workspace dashboard itself.  It must never reuse a contextual
  // entity route saved for another surface, even when the current pathname is
  // an entity detail page and the shell classifies that page as home.
  if (section === "home") return `${root}/home`;
  const entityMatch = pathname.match(/^\/app\/[^/]+\/entities\/([^/]+)/);
  const entityId = entityMatch?.[1];
  if (entityId && ["chat", "calendar", "files"].includes(section)) {
    const targetView = section === "files" ? "file" : section;
    return `/app/${scope}/entities/${entityId}?view=${targetView}`;
  }
  const currentSection = pathname.match(/^\/app\/[^/]+\/([^/]+)/)?.[1];
  const channelId = searchParams.get("channel");
  if (
    channelId &&
    ["chat", "calendar", "files"].includes(section) &&
    ["chat", "calendar", "files"].includes(currentSection ?? "")
  ) {
    if (section === currentSection) return `${root}/${section}`;
    return `${root}/${section}?channel=${encodeURIComponent(channelId)}&source=${encodeURIComponent(currentSection ?? "")}`;
  }
  return fallback;
}

function topLevelSectionHref(scope: string, section: SectionKey) {
  if (section === "chat") {
    return `/app/${scope}/entities`;
  }
  if (section === "calendar" || section === "files") {
    return `/app/${scope}/entities?surface=${section}`;
  }
  return `/app/${scope}/${section}`;
}

function surfaceRouteForSection(scope: string, section: SectionKey, previousRoute: string, previousSection?: SectionKey, selectedCalendarEntity?: string | null) {
  if (!["chat", "calendar", "files"].includes(section)) return null;
  const entityMatch = previousRoute.match(/^\/app\/[^/]+\/entities\/([^/?]+)/);
  // An entity route is authoritative when the user is already viewing a
  // specific channel. Only fall back to the calendar selection list for the
  // aggregate calendar route, where there is no entity in the URL.
  const entityId = entityMatch?.[1] ?? selectedCalendarEntity;
  if (entityId) {
    const view = section === "files" ? "file" : section;
    const selection = previousSection && previousSection !== section ? "&selection=previous" : "";
    return `/app/${encodeURIComponent(scope)}/entities/${entityId}?view=${view}${selection}`;
  }
  const channel = previousRoute.match(/[?&]channel=([^&]+)/)?.[1];
  if (channel) {
    const target = section === "files" ? "files" : section === "calendar" ? "calendar" : "chat";
    const source = previousRoute.includes("/calendar") ? "calendar" : previousRoute.includes("/files") ? "files" : "chat";
    return `/app/${encodeURIComponent(scope)}/${target}?channel=${channel}&source=${source}`;
  }
  return null;
}

